import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { EwentsTooltipDirective } from '@ewentscompany/ewents-components';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { LoginService, User } from '../../auth/login.service';
import { PortalCompanyNavService } from '../../shared/services/portal-company-nav.service';
import { CustomSvgIconName } from '../../shared/constants/custom-icons.constants';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { AvatarComponent } from '@ewentscompany/ewents-components';
import { PERMISSIONS } from '@ewentscompany/ewents-settings';
import { TranslatePipe } from '@ngx-translate/core';

interface MainMenuChild {
  label: string;
  route: string;
  icon: CustomSvgIconName;
  activeIcon: CustomSvgIconName;
  requiredPermission?: string;
}

interface MainMenuItem {
  label: string;
  route?: string;
  icon: CustomSvgIconName;
  activeIcon: CustomSvgIconName;
  isHovered: boolean;
  /** When set, item only shows if user has the permission (or ADMIN). */
  requiredPermission?: string;
  /** Group secondary destinations under one top-level control. */
  children?: MainMenuChild[];
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    EwentsTooltipDirective,
    RouterModule,
    NotificationBellComponent,
    AvatarComponent,
    TranslatePipe,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private static readonly MOBILE_BREAKPOINT_PX = 900;

  private readonly loginService = inject(LoginService);
  private readonly router = inject(Router);
  private readonly companyNav = inject(PortalCompanyNavService);

  currentUser: User | null = null;
  isMobileMenuOpen = signal(false);
  companySectionOpen = signal(false);

  readonly mainMenu: MainMenuItem[] = [
    {
      label: 'nav.timesheets',
      route: '/timesheets',
      icon: 'alarm-outlined',
      activeIcon: 'alarm-filled',
      isHovered: false,
    },
    {
      label: 'nav.projects',
      route: '/assignments',
      icon: 'folder-outlined',
      activeIcon: 'folder-filled',
      isHovered: false,
    },
    {
      label: 'nav.coworkers',
      route: '/coworkers',
      icon: 'groups-outlined',
      activeIcon: 'groups-filled',
      isHovered: false,
    },
    {
      label: 'nav.company',
      icon: 'company',
      activeIcon: 'company-filled',
      isHovered: false,
      children: [
        {
          label: 'nav.orgChart',
          route: '/org-chart',
          icon: 'org-chart',
          activeIcon: 'org-chart-filled',
        },
        {
          label: 'nav.allHands',
          route: '/all-hands',
          icon: 'all-hands',
          activeIcon: 'all-hands-filled',
        },
        {
          label: 'nav.policies',
          route: '/policies/policies',
          icon: 'policies',
          activeIcon: 'policies-filled',
          requiredPermission: PERMISSIONS.COMPLIANCE.ACKNOWLEDGE,
        },
      ],
    },
  ];

  private routerSub?: Subscription;

  readonly filteredMainMenuItems = computed(() => {
    this.companyNav.loaded();
    if (!this.loginService.hasUsableSession()) {
      return [];
    }
    return this.mainMenu
      .map(item => {
        if (!item.children?.length) {
          if (!item.requiredPermission) return item;
          const ok = this.loginService.hasAnyPermission([
            item.requiredPermission,
            PERMISSIONS.GLOBAL.ADMIN,
          ]);
          return ok ? item : null;
        }
        const children = item.children.filter(child => {
          if (!this.companyNav.isRouteVisible(child.route)) return false;
          if (!child.requiredPermission) return true;
          return this.loginService.hasAnyPermission([
            child.requiredPermission,
            PERMISSIONS.GLOBAL.ADMIN,
          ]);
        });
        if (!children.length) return null;
        return { ...item, children };
      })
      .filter((item): item is MainMenuItem => item != null);
  });

  readonly companyMenuItem = computed(
    () => this.filteredMainMenuItems().find(item => !!item.children?.length) ?? null
  );

  constructor() {}

  ngOnInit() {
    this.companyNav.ensureLoaded();

    this.loginService.getCurrentUser$().subscribe(user => {
      this.currentUser = user;
      if (user) this.companyNav.ensureLoaded();
    });

    if (this.loginService.isAuthenticated() && !this.loginService.getUser()) {
      this.loginService.fetchCurrentUser().subscribe({
        next: user => {
          this.currentUser = user;
        },
        error: () => {},
      });
    }

    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.closeMobileMenu());
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen.update(open => !open);
    if (!this.isMobileMenuOpen()) {
      this.companySectionOpen.set(false);
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
    this.companySectionOpen.set(false);
  }

  toggleCompanySection() {
    this.companySectionOpen.update(open => !open);
  }

  onLogout() {
    this.loginService.logout();
  }

  onMobileLogout() {
    this.closeMobileMenu();
    this.onLogout();
  }

  onMobileNavClick() {
    this.closeMobileMenu();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (window.innerWidth > HeaderComponent.MOBILE_BREAKPOINT_PX) {
      this.closeMobileMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeMobileMenu();
  }

  getMainMenuIcon(
    item: MainMenuItem | MainMenuChild,
    isActiveOrHovered: boolean
  ): CustomSvgIconName {
    return isActiveOrHovered ? item.activeIcon : item.icon;
  }

  isChildActive(item: MainMenuItem): boolean {
    if (!item.children?.length) return false;
    const url = this.router.url.split('?')[0];
    return item.children.some(child => {
      if (child.route === '/policies' || child.route.startsWith('/policies/')) {
        return url === '/policies' || url.startsWith('/policies/');
      }
      return url === child.route || url.startsWith(`${child.route}/`);
    });
  }

  isRouteActive(route: string): boolean {
    const url = this.router.url.split('?')[0];
    if (route === '/policies' || route === '/policies/policies') {
      return url === '/policies' || url === '/policies/policies';
    }
    return url === route || url.startsWith(`${route}/`);
  }

  navigateCompany(route: string): void {
    void this.router.navigateByUrl(route);
  }

  getDisplayName(): string {
    if (!this.currentUser) return 'Demo User';
    return `${this.currentUser.firstName} ${this.currentUser.lastName}`;
  }
}
